/**
 * Core Authentication Service
 * Handles login, logout, session management
 * WBC3 Compliant: Audit trail for all auth events
 *
 * PHASE 3.6 TIER 1: IndexedDB Migration (Fire-and-Forget Pattern)
 * - Added parallel IndexedDB writes for RBAC bridge (current_user)
 * - Maintains synchronous operation (auth can't be async)
 * - Fire-and-forget: IndexedDB write failures don't block auth
 * - localStorage remains primary source of truth for session/auth
 */

import { readJSON, writeJSON } from '../../lib/storage';
import { hashPassword, verifyPassword } from './passwordUtils';
import * as storageRouter from '../../lib/storageRouter';

// Storage keys
const STORAGE_KEYS = {
  USERS: 'sr.auth.users',
  SESSION: 'sr.auth.session',
  CONFIG: 'sr.auth.config'
};

// Session duration: 8 hours default, 30 days with remember me
const SESSION_DURATION = {
  DEFAULT: 8 * 60 * 60 * 1000,       // 8 hours
  REMEMBER_ME: 30 * 24 * 60 * 60 * 1000  // 30 days
};

// Role hierarchy (higher can do what lower can)
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN', // SeaReady support - can impersonate any org
  ADMIN: 'ADMIN',     // Organization administrator, shore-based
  MASTER: 'MASTER',   // Vessel master/captain
  OFFICER: 'OFFICER', // Watch officers
  CREW: 'CREW'        // Basic crew access
};

// Role mapping: Auth system (uppercase) -> RBAC system (lowercase)
// RBAC uses: 'fleet' (admin), 'skipper' (master/officer), 'crew'
const AUTH_TO_RBAC_ROLES = {
  'SUPER_ADMIN': 'super_admin',  // New role for Jonathan
  'ADMIN': 'fleet',
  'OWNER': 'fleet',  // Company owner = fleet admin
  'MASTER': 'skipper',
  'OFFICER': 'skipper',  // Officers mapped to skipper-level RBAC
  'CREW': 'crew'
};

/**
 * Bridge auth user to RBAC system
 * usePermissions.js reads from 'current_user' localStorage key
 *
 * PHASE 3.6 TIER 1: Added parallel IndexedDB writes (fire-and-forget)
 * - Critical RBAC bridge: auth role → permissions system
 * - Synchronous localStorage write (session init can't be async)
 * - Async IndexedDB write runs in background (doesn't block auth flow)
 *
 * @param {Object} user - User object from auth system
 */
export function setRBACUser(user) {
  if (!user) {
    localStorage.removeItem('current_user');

    // PHASE 3.6 TIER 1: Parallel IndexedDB removal (fire-and-forget)
    storageRouter.removeJSON('current_user').catch(error => {
      console.warn('[authService] Parallel IndexedDB removal failed for current_user:', error);
      // localStorage removal succeeded, so auth flow continues normally
    });

    return;
  }

  const rbacUser = {
    id: user.id,
    name: user.name,
    role: AUTH_TO_RBAC_ROLES[user.role?.toUpperCase()] || user.role || 'crew',
    email: user.email,
    organizationId: user.organizationId || null,  // NEW
    organizationName: user.organizationName || null,  // NEW
    vesselIds: user.vessels || user.vesselIds || [],
    signatureImage: user.signatureImage || null  // FT-132: Include saved signature
  };

  localStorage.setItem('current_user', JSON.stringify(rbacUser));

  // PHASE 3.6 TIER 1: Parallel IndexedDB write (fire-and-forget)
  storageRouter.writeJSON('current_user', rbacUser).catch(error => {
    console.warn('[authService] Parallel IndexedDB write failed for current_user:', error);
    // localStorage write succeeded, so auth flow continues normally
  });
}

/**
 * Log an audit event for security actions
 */
const logAuthEvent = (action, result, userId = null, metadata = {}) => {
  try {
    const events = readJSON('sr.audit.events', []);
    events.push({
      id: `auth_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
      timestamp: Date.now(),
      type: 'security',
      module: 'auth',
      action,
      result,
      userId,
      metadata
    });

    // Keep last 1000 events
    if (events.length > 1000) {
      events.splice(0, events.length - 1000);
    }

    writeJSON('sr.audit.events', events);
  } catch (error) {
    console.error('Failed to log auth event:', error);
  }
};

/**
 * Login user with email and password
 * @param {string} email
 * @param {string} password
 * @param {boolean} rememberMe
 * @returns {Promise<{success: boolean, user?: object, session?: object, error?: string}>}
 */
export const login = async (email, password, rememberMe = false) => {
  const users = readJSON(STORAGE_KEYS.USERS, []);
  const user = users.find(u => u.email.toLowerCase() === email.toLowerCase() && u.isActive);

  if (!user) {
    logAuthEvent('login_failed', 'failure', null, { email, reason: 'user_not_found' });
    return { success: false, error: 'Invalid email or password' };
  }

  const validPassword = await verifyPassword(password, user.passwordHash);

  if (!validPassword) {
    logAuthEvent('login_failed', 'failure', user.id, { email, reason: 'invalid_password' });
    return { success: false, error: 'Invalid email or password' };
  }

  // Create session
  const sessionDuration = rememberMe ? SESSION_DURATION.REMEMBER_ME : SESSION_DURATION.DEFAULT;
  const session = {
    userId: user.id,
    token: `sess_${Date.now()}_${Math.random().toString(36).substring(2)}`,
    role: user.role,
    userName: user.name,
    userEmail: user.email,
    createdAt: Date.now(),
    expiresAt: Date.now() + sessionDuration,
    rememberMe
  };

  // Update user last login
  const updatedUsers = users.map(u =>
    u.id === user.id ? { ...u, lastLogin: Date.now() } : u
  );

  writeJSON(STORAGE_KEYS.USERS, updatedUsers);
  writeJSON(STORAGE_KEYS.SESSION, session);

  // Bridge to RBAC system
  setRBACUser(user);

  logAuthEvent('login', 'success', user.id, { role: user.role, rememberMe });

  // Return user without password hash
  const { passwordHash, ...safeUser } = user;
  return {
    success: true,
    user: safeUser,
    session
  };
};

/**
 * Logout current user
 *
 * PHASE 3.6 TIER 1: Added parallel IndexedDB removal
 */
export const logout = () => {
  const session = getCurrentSession();

  if (session) {
    logAuthEvent('logout', 'success', session.userId);
  }

  writeJSON(STORAGE_KEYS.SESSION, null);

  // Clear RBAC user (localStorage - synchronous)
  localStorage.removeItem('current_user');

  // PHASE 3.6 TIER 1: Parallel IndexedDB removal (fire-and-forget)
  storageRouter.removeJSON('current_user').catch(error => {
    console.warn('[authService] Parallel IndexedDB removal failed for current_user on logout:', error);
    // localStorage removal succeeded, so logout is successful
  });
};

/**
 * Get current session (null if expired)
 * @returns {object|null}
 */
export const getCurrentSession = () => {
  const session = readJSON(STORAGE_KEYS.SESSION, null);

  if (!session) return null;

  // Check expiration
  if (session.expiresAt < Date.now()) {
    writeJSON(STORAGE_KEYS.SESSION, null);
    return null;
  }

  return session;
};

/**
 * Get current user from session
 * @returns {object|null}
 */
export const getCurrentUser = () => {
  const session = getCurrentSession();

  if (!session) return null;

  const users = readJSON(STORAGE_KEYS.USERS, []);
  const user = users.find(u => u.id === session.userId);

  if (!user || !user.isActive) {
    writeJSON(STORAGE_KEYS.SESSION, null);
    return null;
  }

  // Return without password hash
  const { passwordHash, ...safeUser } = user;
  return safeUser;
};

/**
 * Check if this is first run (no users exist)
 * @returns {boolean}
 */
export const isFirstRun = () => {
  const users = readJSON(STORAGE_KEYS.USERS, []);
  return users.length === 0;
};

/**
 * Check if auth is enabled (setup completed)
 * @returns {boolean}
 */
export const isAuthEnabled = () => {
  const config = readJSON(STORAGE_KEYS.CONFIG, null);
  return config?.authEnabled === true;
};

/**
 * Enable authentication system
 */
export const enableAuth = () => {
  const config = readJSON(STORAGE_KEYS.CONFIG, {});
  writeJSON(STORAGE_KEYS.CONFIG, {
    ...config,
    authEnabled: true,
    enabledAt: Date.now()
  });
  logAuthEvent('auth_enabled', 'success', null, {});
};

/**
 * Create initial admin account (setup wizard)
 * @param {string} name
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>}
 */
export const createInitialAdmin = async (name, email, password) => {
  const users = readJSON(STORAGE_KEYS.USERS, []);

  if (users.length > 0) {
    throw new Error('Admin account already exists');
  }

  const passwordHash = await hashPassword(password);

  const admin = {
    id: `usr_${Date.now()}`,
    name,
    email,
    role: ROLES.ADMIN,
    organizationId: `org_${Date.now()}`,  // NEW: Create org for this admin
    organizationName: 'Default Organization',  // NEW: Can be updated later
    passwordHash,
    createdAt: Date.now(),
    createdBy: 'system',
    isActive: true
  };

  writeJSON(STORAGE_KEYS.USERS, [admin]);
  writeJSON(STORAGE_KEYS.CONFIG, {
    setupCompleted: true,
    setupCompletedAt: Date.now(),
    authEnabled: true
  });

  logAuthEvent('initial_setup', 'success', admin.id, { adminEmail: email });

  // Return without password hash
  const { passwordHash: _, ...safeAdmin } = admin;
  return safeAdmin;
};

/**
 * Check if user has required role or higher
 * @param {object} user
 * @param {string} requiredRole
 * @returns {boolean}
 */
export const hasRole = (user, requiredRole) => {
  if (!user) return false;

  const roleHierarchy = {
    [ROLES.SUPER_ADMIN]: 5,  // Highest level
    [ROLES.ADMIN]: 4,
    [ROLES.MASTER]: 3,
    [ROLES.OFFICER]: 2,
    [ROLES.CREW]: 1
  };

  const userLevel = roleHierarchy[user.role] || 0;
  const requiredLevel = roleHierarchy[requiredRole] || 0;

  return userLevel >= requiredLevel;
};

/**
 * Get all users (admin only)
 * @returns {Array}
 */
export const getAllUsers = () => {
  const users = readJSON(STORAGE_KEYS.USERS, []);
  // Return without password hashes
  return users.map(({ passwordHash, ...user }) => user);
};

/**
 * Create a new user (admin only)
 * @param {object} userData
 * @param {string} password
 * @param {string} createdByUserId
 * @returns {Promise<object>}
 */
export const createUser = async (userData, password, createdByUserId) => {
  const users = readJSON(STORAGE_KEYS.USERS, []);

  // Check duplicate email
  if (users.find(u => u.email.toLowerCase() === userData.email.toLowerCase())) {
    throw new Error('Email already exists');
  }

  const passwordHash = await hashPassword(password);

  const newUser = {
    id: `usr_${Date.now()}`,
    ...userData,
    organizationId: userData.organizationId || null,  // NEW - optional for now
    organizationName: userData.organizationName || null,  // NEW - optional for now
    passwordHash,
    createdAt: Date.now(),
    createdBy: createdByUserId,
    isActive: true
  };

  writeJSON(STORAGE_KEYS.USERS, [...users, newUser]);

  logAuthEvent('user_created', 'success', createdByUserId, {
    newUserId: newUser.id,
    newUserRole: newUser.role,
    newUserEmail: newUser.email
  });

  const { passwordHash: _, ...safeUser } = newUser;
  return safeUser;
};

/**
 * Update user (admin only, or self for profile)
 * @param {string} userId
 * @param {object} updates
 * @param {string} updatedByUserId
 * @returns {object}
 */
export const updateUser = (userId, updates, updatedByUserId) => {
  const users = readJSON(STORAGE_KEYS.USERS, []);

  // Don't allow updating password through this method
  const { password, passwordHash, ...safeUpdates } = updates;

  const updatedUsers = users.map(u =>
    u.id === userId
      ? { ...u, ...safeUpdates, modifiedAt: Date.now(), modifiedBy: updatedByUserId }
      : u
  );

  writeJSON(STORAGE_KEYS.USERS, updatedUsers);

  logAuthEvent('user_updated', 'success', updatedByUserId, {
    updatedUserId: userId,
    changes: Object.keys(safeUpdates)
  });

  const updatedUser = updatedUsers.find(u => u.id === userId);
  const { passwordHash: _, ...safeUser } = updatedUser;
  return safeUser;
};

/**
 * Deactivate user (admin only)
 * @param {string} userId
 * @param {string} deactivatedByUserId
 */
export const deactivateUser = (userId, deactivatedByUserId) => {
  updateUser(userId, { isActive: false }, deactivatedByUserId);
  logAuthEvent('user_deactivated', 'success', deactivatedByUserId, { deactivatedUserId: userId });
};

/**
 * Reactivate user (admin only)
 * @param {string} userId
 * @param {string} reactivatedByUserId
 */
export const reactivateUser = (userId, reactivatedByUserId) => {
  updateUser(userId, { isActive: true }, reactivatedByUserId);
  logAuthEvent('user_reactivated', 'success', reactivatedByUserId, { reactivatedUserId: userId });
};

/**
 * Change user password
 * @param {string} userId
 * @param {string} newPassword
 * @param {string} changedByUserId
 */
export const changePassword = async (userId, newPassword, changedByUserId) => {
  const users = readJSON(STORAGE_KEYS.USERS, []);
  const passwordHash = await hashPassword(newPassword);

  const updatedUsers = users.map(u =>
    u.id === userId
      ? { ...u, passwordHash, modifiedAt: Date.now(), modifiedBy: changedByUserId }
      : u
  );

  writeJSON(STORAGE_KEYS.USERS, updatedUsers);

  logAuthEvent('password_changed', 'success', changedByUserId, { targetUserId: userId });
};

/**
 * Extend session (for activity-based refresh)
 */
export const extendSession = () => {
  const session = getCurrentSession();
  if (!session) return;

  const extension = session.rememberMe ? SESSION_DURATION.REMEMBER_ME : SESSION_DURATION.DEFAULT;

  writeJSON(STORAGE_KEYS.SESSION, {
    ...session,
    expiresAt: Date.now() + extension
  });
};

/**
 * Emergency password reset for admin user
 * Call this from browser console: window.__resetAdminPassword('NewPassword123')
 */
export const resetAdminPassword = async (newPassword) => {
  const users = readJSON(STORAGE_KEYS.USERS, []);
  if (users.length === 0) return { success: false, error: 'No users found' };

  const newHash = await hashPassword(newPassword);
  users[0].passwordHash = newHash;
  writeJSON(STORAGE_KEYS.USERS, users);

  logAuthEvent('emergency_password_reset', 'success', users[0].id, { email: users[0].email });

  return { success: true, email: users[0].email };
};

// Expose to window for emergency use
if (typeof window !== 'undefined') {
  window.__resetAdminPassword = resetAdminPassword;
}
